/*
 * File: AddUsers.jsx
 * Project: lets-talk-web
 *
 * Created by Morgan Rivera on March 27, 2026 at 1:04 PM
 *
 */

/**
 * Imports
 */

// Modules
import React, {
	useEffect, useRef, useState
} from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

// Services
import { addUsers } from '../../../services/admin';

// Utilities
import { createStateLocale } from '../../../utilities/locale';
import { toastError, toastSuccess } from '../../../utilities/toaster';

// Components
import {
	Meta,
	AppNavigation,
	Typography,
	TextInput
} from '../../../components';

// Styles
import * as S from './AddUsers.styles';

/**
 * Component
 */

const AddUsers = ({ meta, locale, data }) => {

	// Set state
	const [isLoading, setIsLoading] = useState(false);
	const [inputValues, setInputValues] = useState({});


	// Get current locale from hook
	const clientLocale = useSelector((state) => state.locale.value);
	const stateLocale = createStateLocale(clientLocale, locale);

	// Get ui mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Create reference for components
	const isMounted = useRef(true);

	// Handle add users
	const handleAddUsers = async () => {

		// Get parameters
		const { email, role } = inputValues;

		// Validate parameters
		if (!email) {
			toastError(uiMode, 'An email is required');
			return;
		}

		if (!role) {
			toastError(uiMode, 'A role is required');
			return;
		}


		// Set loading state
		setIsLoading(true);

		// Begin add user process
		addUsers({ email, role }).then(() => {

			// Show success toast
			toastSuccess(uiMode, 'Success! The user has been sent an access code.', { duration: 3000 });

			// Set isLoading to false after 1 second
			setTimeout(() => {
				setIsLoading(false);
			}, 1000);

		}).catch((err) => {

			setIsLoading(false);

			// Show error message
			const message = err?.response?.data?.message || 'Whoops. An error has occurred.';
			toastError(uiMode, message);

		});
	};

	// Handle component initialization
	useEffect(() => {

		// Set state
		isMounted.current = true;

		// Handle actions on dismount
		return () => { isMounted.current = false; };

	}, []);

	// Handle input
	const handleOnChange = (e) => {
		const { name, value } = e.target;

		setInputValues((prev) => ({
			...prev,
			[name]: value
		}));
	};


	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} locale={stateLocale} />

			{/* Component Content */}
			<AppNavigation data={data}>
				<S.Wrapper left right>

					{/* Title Widget */}
					<S.TitleWidget>
						<Typography tag="h4" weight="medium">Add New Users</Typography>
					</S.TitleWidget>

					<br />

					{/* Content */}
					<S.ContentContainer>
						<Typography tag="p" variation="1" weight="regular">
							Enter user info of staff/research participant or free parent to send them an access code.
						</Typography>
					</S.ContentContainer>

					{/* Email Input */}
					<S.GroupLabel>Email:</S.GroupLabel>
					<TextInput
						name="email"
						type="email"
						value={inputValues.email || ''}
						autoComplete="email"
						onChange={handleOnChange}
					/>

					<br />
					{/* Role Selection */}
					<S.RadioGroup>
						<S.GroupLabel>User Role:</S.GroupLabel>

						<S.RadioLabel>
							<input
								type="radio"
								name="role"
								value="research_participant"
								checked={inputValues.role === 'research_participant'}
								onChange={handleOnChange}
							/>
							Research Participant
						</S.RadioLabel>

						<S.RadioLabel>
							<input
								type="radio"
								name="role"
								value="staff"
								checked={inputValues.role === 'staff'}
								onChange={handleOnChange}
							/>
							Staff
						</S.RadioLabel>

						<S.RadioLabel>
							<input
								type="radio"
								name="role"
								value="free_parent"
								checked={inputValues.role === 'free_parent'}
								onChange={handleOnChange}
							/>
							Free Parent
						</S.RadioLabel>
					</S.RadioGroup>

					<br />
					{/* Send Button */}
					<S.ActionButton
						disabled={isLoading}
						isLoading={isLoading}
						onClick={handleAddUsers}
						size="large"
					>
						Add User
					</S.ActionButton>


				</S.Wrapper>
			</AppNavigation>
		</>
	);
};


/**
 * Configuration
 */

AddUsers.propTypes = {
	meta: PropTypes.shape(),
	locale: PropTypes.shape(),
	data: PropTypes.shape(),
};
AddUsers.defaultProps = {
	meta: {},
	locale: {},
	data: null
};


/**
 * Exports
 */

export default AddUsers;
